// Draft ATW route vocabulary (../atw/spec.md §§3–4). This is NOT a route resolver or a
// client policy evaluator. The collection is configuration, not a fourth Szzt collection.
import { COLLECTION } from "./resolve.ts";
import { BLOB_STAGING_COLLECTION } from "./staging.ts";

export interface RouteTarget {
  uri: string;
  mirrors?: string[];
  [key: string]: unknown;
}

export interface RoutePolicy {
  remoteSubresources?: boolean;
  allowedOrigins?: string[];
  critical?: string[];
  [key: string]: unknown;
}

interface RouteBase {
  $type: string;
  policy?: RoutePolicy;
  createdAt?: string;
  successor?: string;
  [key: string]: unknown;
}

export type RouteRecord = RouteBase & (
  | { status?: "active"; targets: RouteTarget[]; alias?: never }
  | { status?: "active"; alias: string; targets?: never }
  | { status: "retired"; targets?: never; alias?: never }
);

export class RouteValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "RouteValidationError";
  }
}

function requireShape(ok: boolean, message: string): asserts ok {
  if (!ok) throw new RouteValidationError(message);
}

/** AT Protocol record keys are ASCII and case-sensitive. `self` is valid (the root name). */
export function assertRouteName(name: string): void {
  requireShape(
    /^[A-Za-z0-9._~:-]{1,512}$/.test(name) && name !== "." && name !== "..",
    "short name must be a 1–512 character AT Protocol record key, not '.' or '..'",
  );
}

function assertNsid(collection: string): void {
  const parts = collection.split(".");
  const domainLabel = /^[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?$/;
  requireShape(
    collection.length <= 317 && parts.length >= 3 &&
      /^[A-Za-z]/.test(parts[0] ?? "") &&
      parts.slice(0, -1).every((p) => domainLabel.test(p)) &&
      /^[A-Za-z]{1,63}$/.test(parts.at(-1) ?? ""),
    "collection must be an explicit valid NSID (the draft's route NSID is not settled)",
  );
}

/** Validate an explicit NSID; never let route operations overwrite Szzt's storage collections. */
export function assertRouteCollection(collection: string): void {
  assertNsid(collection);
  requireShape(
    collection !== BLOB_STAGING_COLLECTION && !Object.values(COLLECTION).some((c) => c === collection),
    "route collection must not be a Szzt site, deployment, MASL, or staging collection",
  );
}

function object(value: unknown, what: string): Record<string, unknown> {
  requireShape(typeof value === "object" && value !== null && !Array.isArray(value), `${what} must be an object`);
  return value as Record<string, unknown>;
}

function uri(value: unknown, what: string): asserts value is string {
  requireShape(typeof value === "string" && value.length > 0 && !/[\s\u0000-\u001f\u007f]/u.test(value), `${what} must be an absolute URI`);
  // WHATWG URL treats the colons in an AT-URI's DID authority as a port. AT Protocol
  // record URIs have their own grammar; never run them through that authority parser.
  if (value.startsWith("at://")) {
    assertRouteSuccessor(value);
    return;
  }
  try {
    new URL(value);
  } catch {
    throw new RouteValidationError(`${what} must be an absolute URI`);
  }
}

function strings(value: unknown, max: number, what: string, check: (s: unknown, what: string) => void): void {
  requireShape(Array.isArray(value) && value.length <= max, `${what} must be an array of at most ${max} entries`);
  for (const item of value) check(item, what);
}

/** Validate known policy fields, retaining unknown restrictions for future clients to interpret.
 * Unknown critical keys are legal to PUBLISH; clients that don't implement them must fail closed. */
export function validateRoutePolicy(value: unknown): RoutePolicy {
  const p = object(value, "policy");
  if ("remoteSubresources" in p) requireShape(typeof p.remoteSubresources === "boolean", "policy.remoteSubresources must be boolean");
  if ("allowedOrigins" in p) strings(p.allowedOrigins, 32, "policy.allowedOrigins", uri);
  if ("critical" in p) strings(p.critical, 32, "policy.critical", (s) => {
    requireShape(typeof s === "string" && s.length <= 128, "policy.critical keys must be strings of at most 128 characters");
  });
  return p as RoutePolicy;
}

/** Validate an AT-URI with all three components, not a subpath/query/fragment-bearing web link. */
export function assertRouteSuccessor(value: unknown): asserts value is string {
  requireShape(typeof value === "string", "successor must be a record AT-URI");
  const m = /^at:\/\/([^/?#\s]+)\/([^/?#\s]+)\/([^/?#\s]+)$/.exec(value);
  requireShape(m !== null, "successor must be a record AT-URI");
  assertRouteName(m[3]!);
  // A successor may name another collection, including app.szzt.site; only validate NSID syntax.
  assertNsid(m[2]!);
}

/** Structural validation only. Unknown record/target fields pass through untouched. No network,
 * alias lookup, handler selection, proof verification, or widening of a client's defaults here. */
export function validateRouteRecord(value: unknown, collection: string): RouteRecord {
  assertRouteCollection(collection);
  const r = object(value, "route");
  requireShape(r.$type === collection, "route $type must match the configured collection");
  requireShape(r.status === undefined || r.status === "active" || r.status === "retired", "unknown route status");
  const targets = "targets" in r;
  const alias = "alias" in r;
  if (r.status === "retired") {
    requireShape(!targets && !alias, "retired routes must have neither targets nor alias");
  } else {
    requireShape(targets !== alias, "active routes must have exactly one of targets or alias");
    if (alias) {
      requireShape(typeof r.alias === "string", "alias must be a short name in the same repo");
      assertRouteName(r.alias);
    } else {
      requireShape(Array.isArray(r.targets) && r.targets.length >= 1 && r.targets.length <= 16, "targets must contain 1–16 entries");
      for (const value of r.targets) {
        const t = object(value, "target");
        uri(t.uri, "target.uri");
        if ("mirrors" in t) strings(t.mirrors, 8, "target.mirrors", uri);
      }
    }
  }
  if ("policy" in r) validateRoutePolicy(r.policy);
  if ("successor" in r) assertRouteSuccessor(r.successor);
  if ("createdAt" in r) requireShape(
    typeof r.createdAt === "string" && /^\d{4}-\d{2}-\d{2}T/.test(r.createdAt) && Number.isFinite(Date.parse(r.createdAt)),
    "createdAt must be a datetime string",
  );
  return r as RouteRecord;
}
